/* eslint-disable no-undef */
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Spin } from 'antd';
import { Search, ChevronRight, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import MobileSheetBase from './MobileSheetBase';

const MobileTreeSheet = ({
  isOpen,
  onClose,
  onDone,
  title,
  treeData = [],
  value = [],
  onChange,
  isMulti = false,
  loading = false,
  onSearch,
  maxCount,
  languageSelector,
}) => {
  const { t } = useTranslation('common');
  const [searchValue, setSearchValue] = useState('');
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [localValue, setLocalValue] = useState(value);
  const contentRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
    } else {
      setLocalValue(value);
    }
  }, [isOpen, value]);

  const debouncedSearch = useCallback(
    debounce((term) => {
      if (onSearch) {
        onSearch(term);
      }
    }, 300),
    [onSearch]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = useCallback((e) => {
    const term = e.target.value;
    setSearchValue(term);
    debouncedSearch(term);
  }, [debouncedSearch]);

  const nodeMap = useMemo(() => {
    const map = new Map();
    const addNodes = (nodes) => {
      if (!nodes) return;
      nodes.forEach((node) => {
        const id = node.value || node.id;
        if (id) map.set(id, node);
        if (node.children?.length > 0) addNodes(node.children);
      });
    };
    addNodes(treeData);
    return map;
  }, [treeData]);

  const filteredTree = useMemo(() => {
    if (!searchValue) return treeData;
    const searchLower = searchValue.toLowerCase();

    const filterNode = (node) => {
      const nodeTitle = String(node.title || '').toLowerCase();
      const frenchName = String(node.french_name || '').toLowerCase();
      const englishName = String(node.english_name || '').toLowerCase();
      const latinName = String(node.latin_name || '').toLowerCase();

      const nodeMatches =
        nodeTitle.includes(searchLower) ||
        frenchName.includes(searchLower) ||
        englishName.includes(searchLower) ||
        latinName.includes(searchLower);

      if (!node.children || node.children.length === 0) {
        return nodeMatches ? node : null;
      }

      const filteredChildren = node.children.map(filterNode).filter(Boolean);

      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }

      return nodeMatches ? { ...node, children: [] } : null;
    };

    return treeData.map(filterNode).filter(Boolean);
  }, [treeData, searchValue]);

  useEffect(() => {
    if (searchValue) {
      const keysToExpand = new Set();
      const collectParentKeys = (nodes) => {
        nodes.forEach((node) => {
          const nodeId = node.value || node.id;
          if (node.children?.length > 0) {
            keysToExpand.add(nodeId);
            collectParentKeys(node.children);
          }
        });
      };
      collectParentKeys(filteredTree);
      setExpandedKeys(keysToExpand);
    }
  }, [searchValue, filteredTree]);

  const toggleExpand = useCallback((nodeId) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const handleSelectNode = useCallback((nodeId, node) => {
    if (isMulti) {
      setLocalValue((prev) => {
        const prevArray = Array.isArray(prev) ? prev : [];
        const isSelected = prevArray.includes(nodeId);

        if (isSelected) {
          return prevArray.filter((id) => id !== nodeId);
        } else {
          if (maxCount && prevArray.length >= maxCount) {
            return prev;
          }
          return [...prevArray, nodeId];
        }
      });
    } else {
      onChange([nodeId], [node]);
      onClose();
    }
  }, [isMulti, maxCount, onChange, onClose]);

  const handleDone = useCallback(() => {
    const enrichedNodes = localValue.map((val) => {
      const node = nodeMap.get(val);
      return {
        value: val,
        label: node?.title || val,
        key: val,
        title: node?.title || val,
        french_name: node?.french_name,
        english_name: node?.english_name,
        latin_name: node?.latin_name,
        ...(node || {}),
      };
    });

    onChange(localValue, enrichedNodes);
    if (onDone) onDone();
    onClose();
  }, [localValue, nodeMap, onChange, onDone, onClose]);

  const renderNode = useCallback((node, depth = 0) => {
    const nodeId = node.value || node.id;
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedKeys.has(nodeId);
    const isSelected = Array.isArray(localValue) && localValue.includes(nodeId);
    const paddingLeft = 12 + depth * 12;

    if (hasChildren) {
      return (
        <div key={nodeId}>
          <div
            className="flex items-center min-h-[40px] py-2 cursor-pointer bg-gray-50 border-b border-gray-100"
            style={{ paddingLeft }}
            onClick={() => toggleExpand(nodeId)}
          >
            <div
              className={`w-5 h-5 flex items-center justify-center mr-2 text-[#207d54] transition-transform duration-200 ${
                isExpanded ? 'rotate-90' : ''
              }`}
            >
              <ChevronRight size={14} />
            </div>
            <span className="text-xs font-bold text-[#207d54]">{node.title}</span>
          </div>

          {isExpanded && (
            <div>
              {node.children.map((child) => renderNode(child, depth + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        key={nodeId}
        className={`flex items-center min-h-[40px] py-2 cursor-pointer ${
          isSelected ? 'bg-green-50' : ''
        }`}
        style={{ paddingLeft: paddingLeft + 24 }}
        onClick={() => handleSelectNode(nodeId, node)}
      >
        {isMulti ? (
          <div
            className={`w-4 h-4 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
              isSelected ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
            }`}
          >
            {isSelected && <Check size={10} color="#fff" />}
          </div>
        ) : (
          <div
            className={`w-4 h-4 border-2 rounded-full mr-3 flex-shrink-0 flex items-center justify-center ${
              isSelected ? 'border-[#207d54]' : 'border-gray-300'
            }`}
          >
            {isSelected && <div className="w-2 h-2 bg-[#207d54] rounded-full" />}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-900">{node.title}</div>
        </div>
      </div>
    );
  }, [expandedKeys, localValue, isMulti, toggleExpand, handleSelectNode]);

  const selectedCount = Array.isArray(localValue) ? localValue.length : 0;

  const displayTitle = isMulti && selectedCount > 0
    ? `${title} (${selectedCount}${maxCount ? `/${maxCount}` : ''})`
    : title;

  return (
    <MobileSheetBase
      isOpen={isOpen}
      onClose={onClose}
      title={displayTitle}
      showDone={isMulti}
      onDone={handleDone}
      doneLabel={t('Terminé')}
      doneDisabled={isMulti && selectedCount === 0}
    >
      {/* Language selector */}
      {languageSelector && (
        <div className="px-3 py-2 border-b border-gray-100">
          {languageSelector}
        </div>
      )}

      {/* Search */}
      <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
            placeholder={t('search_placeholder')}
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overscroll-contain" ref={contentRef}>
        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Spin size="default" />
          </div>
        )}

        {/* Empty */}
        {!loading && filteredTree.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
            <div className="text-xs">
              {t('No results found')}
            </div>
          </div>
        )}

        {/* Tree */}
        {!loading && filteredTree.length > 0 && (
          <div className="py-1">
            {filteredTree.map((node) => renderNode(node))}
          </div>
        )}
      </div>
    </MobileSheetBase>
  );
};

MobileTreeSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func,
  title: PropTypes.string.isRequired,
  treeData: PropTypes.array,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
  loading: PropTypes.bool,
  onSearch: PropTypes.func,
  maxCount: PropTypes.number,
  languageSelector: PropTypes.node,
};

export default React.memo(MobileTreeSheet);
